import { ModuleWithProviders, NgModule, inject } from '@angular/core';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule as ACommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TYTCommonModule } from '../common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiModule, ApiUser } from '../api';
import { ValidationModule } from '../validation';
import { ImageCropperModule } from 'ngx-image-cropper';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';

import { LoginComponent } from './login/login.component';
import { AuthGuard, LoginGuard, SplashGuard, NotLoggedInGuard } from './auth-guard';
import { UserService } from './user.service';
import { LoginBounceComponent } from './login-bounce/login-bounce.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { PrivacyIndicatorComponent } from './privacy-indicator.component';
import { ImageUploaderComponent } from './image-uploader/image-uploader.component';
import { ForgotComponent } from './forgot/forgot.component';
import { UserAuthMediatorComponent } from './user-auth-mediator/user-auth-mediator.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SocialSignInComponent } from '@tytapp/user/social-sign-in/social-sign-in.component';
import { AcceptTermsComponent } from '@tytapp/common-ui/accept-terms/accept-terms.component';
import { LoginProviderComponent } from '@tytapp/user/login-provider/login-provider.component';
import { OptinsComponent } from '@tytapp/user/optins/optins.component';
import { SlugService } from '@tytapp/slugs';

const COMPONENTS = [

    /**
     * DIALOGS **********
     * - These must be added to entryComponents below
     */

    LoginComponent,
    ForgotComponent,

    /**
     * WIDGETS **********
     */
    PrivacyIndicatorComponent,
    ImageUploaderComponent,
    SocialSignInComponent,

    /**
     * PAGES ***********
     */

    SignUpComponent,
    OptinsComponent,
    LoginProviderComponent,
    LoginBounceComponent,

    UserAuthMediatorComponent,
    AcceptTermsComponent
];

@NgModule({
    declarations: COMPONENTS,
    imports: [
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        ACommonModule,
        RouterModule,
        ImageCropperModule,
        TYTCommonModule,
        ApiModule,
        ValidationModule,
        RecaptchaModule,
        RecaptchaFormsModule,
        MatIconModule,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatSlideToggleModule
    ],
    exports: COMPONENTS
})
export class UserModule {
    users = inject(UserService);
    slugs = inject(SlugService);

    constructor() {
        this.slugs.registerType<ApiUser>({
            type: 'User',
            friendlyName: 'User',
            getUrl: slug => `/community/profiles/${slug.slug}`, // this needs to be the internal URL, not the facade URL (/@username)
            getContent: async slug => await this.users.getPublicProfile(slug.slug),
            getMetadataForContent: async content => ({
                title: content.username ? `@${content.username}` : `User #${content.id}`,
                url: content.username ? `/@${content.username}` : undefined
            })
        })
    }

    static forRoot(): ModuleWithProviders<UserModule> {
        return {
            ngModule: UserModule,
            providers: [
                UserService,
                AuthGuard,
                LoginGuard,
                SplashGuard,
                NotLoggedInGuard
            ]
        };
    }
}
